/**
 * One client's schedule over a window, as `queryOptions`.
 *
 * Two readers: the client calendar's month grid and `/zadania`'s week strip. The KEY is what makes
 * them one read — both derive their window through `model/scheduleWindow`, so a month the calendar
 * has already fetched is served from cache when the strip asks for a week inside it only if the
 * windows match exactly, and they otherwise sit side by side without either invalidating the other.
 *
 * No repository and no service over TanStack Query: `CLAUDE.md` puts query keys and `queryOptions`
 * in the slice's `api/` segment, and the generated key already carries the params object.
 */

import { queryOptions } from '@tanstack/react-query';

import { getCalendareventCustomerCustomerIdSchedule } from '~/shared/api/generated/endpoints/calendar-event/calendar-event';

import type { ScheduleWindow } from '../model/scheduleWindow';

/** The prefix every schedule read shares. Invalidate it to drop every client's every window. */
export const customerScheduleRoot = ['calendar-event', 'customer-schedule'] as const;

/**
 * The schedule for one client between two instants.
 *
 * `enabled` is the caller's: a surface with no client yet, or one that only wants the read for
 * certain rows, says so through `query`. The window's two strings must already carry their UTC
 * marker — see `scheduleWindow`, which is the only supported way to build one.
 */
export function customerScheduleQueryOptions(
  customerId: number,
  window: ScheduleWindow,
  { enabled = true }: { readonly enabled?: boolean } = {},
) {
  const params = { from: window.from, to: window.to };
  return queryOptions({
    queryKey: [...customerScheduleRoot, customerId, params] as const,
    queryFn: ({ signal }) =>
      getCalendareventCustomerCustomerIdSchedule(customerId, params, undefined, signal),
    enabled: enabled && customerId > 0 && window.from !== '' && window.to !== '',
  });
}
